/**
 * React hooks for device and browser information
 */

import { useState, useEffect, useCallback, useSyncExternalStore } from 'react'
import { getDeviceInfo, getLocale } from '@utils/device'
import type { DeviceInfo } from '@utils/device'
import { MOBILE_MAX_WIDTH, TABLET_MAX_WIDTH } from '@/constants'

/**
 * Hook for device information
 */
export function useDeviceInfo(): DeviceInfo {
  const [deviceInfo] = useState<DeviceInfo>(() => getDeviceInfo())
  return deviceInfo
}

/**
 * Hook for browser locale
 */
export function useLocale(): string {
  const [locale] = useState<string>(() => getLocale())
  return locale
}

/**
 * Hook for window size
 */
export function useWindowSize(): { width: number; height: number } {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  })

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowSize
}

/**
 * Efficient media query hook — only re-renders when the breakpoint is crossed,
 * not on every pixel of resize. Matches CSS behavior exactly.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const mq = window.matchMedia(query)
      mq.addEventListener('change', onStoreChange)
      return () => mq.removeEventListener('change', onStoreChange)
    },
    [query]
  )

  const matches = useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false
  )

  return matches
}

/** Phone only. Use `useIsCompact` for "renders the mobile design". */
export function useIsMobile(breakpoint: number = MOBILE_MAX_WIDTH): boolean {
  return useMediaQuery(`(max-width: ${breakpoint}px)`)
}

/** Tablet only — for the rare case tablet must differ from phone. */
export function useIsTablet(minWidth: number = MOBILE_MAX_WIDTH + 1, maxWidth: number = TABLET_MAX_WIDTH): boolean {
  return useMediaQuery(`(min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`)
}

/** Phone + tablet: everything that uses the mobile design. Matches CSS max-width: 1024px. */
export function useIsCompact(breakpoint: number = TABLET_MAX_WIDTH): boolean {
  return useMediaQuery(`(max-width: ${breakpoint}px)`)
}

export function useIsDesktop(breakpoint: number = 1024): boolean {
  return useMediaQuery(`(min-width: ${breakpoint + 1}px)`)
}
